/**
 * Trading Error Mapper
 *
 * Transforms raw Solana/Meteora errors into user-friendly messages.
 */

// Known Meteora DBC error codes
const METEORA_ERRORS: Record<string, string> = {
  '0x1770': 'Trade amount too large for current pool liquidity. Try a smaller amount.',
  '0x1771': 'Price moved too much during trade. Increase slippage tolerance or try again.',
  '0x1772': 'Insufficient balance for this trade.',
  '0x1773': 'Pool is not active. Trading may be temporarily disabled.',
  '0x1774': 'Invalid pool configuration.',
  '0x1775': 'Slippage tolerance exceeded. Try increasing slippage.',
  'MathOverflow': 'Trade amount too large for current pool liquidity. Try a smaller amount.',
};

// Known Solana RPC errors
const RPC_ERRORS: Record<string, string> = {
  'BlockhashNotFound': 'Transaction expired. Please try again.',
  'TransactionExpiredBlockheightExceededError': 'Transaction expired. Please try again.',
  'block height exceeded': 'Transaction expired. Please try again.',
  'blockhash not found': 'Transaction expired. Please try again.',
  'InstructionError': 'Transaction failed on-chain. Check pool liquidity.',
  'insufficient funds': 'Not enough SOL for network fees. Deposit SOL to your wallet and try again.',
  'Insufficient funds': 'Not enough SOL for network fees. Deposit SOL to your wallet and try again.',
  'AccountNotFound': 'Account not found. The pool may not exist.',
  'InvalidAccountData': 'Invalid pool data. The pool may have migrated.',
};

// Network errors
const NETWORK_ERRORS: Record<string, string> = {
  'ECONNRESET': 'Network connection lost. Please check your connection and try again.',
  'ETIMEDOUT': 'Request timed out. Please try again.',
  'ENOTFOUND': 'Unable to reach the network. Please check your connection.',
  'NetworkError': 'Network error occurred. Please try again.',
  'fetch failed': 'Network request failed. Please try again.',
};

// Jupiter swap errors
const JUPITER_ERRORS: Record<string, string> = {
  'INSUFFICIENT_LIQUIDITY': 'Not enough liquidity for this swap. Try a smaller amount.',
  'ROUTE_NOT_FOUND': 'No swap route found for these tokens. Try a different pair.',
  'SLIPPAGE_EXCEEDED': 'Price moved too much. Try again.',
  'TRANSACTION_EXPIRED': 'Swap transaction expired. Please try again.',
  'STALE_QUOTE': 'Quote expired. Getting a fresh quote...',
  'No route found': 'No swap route found for these tokens.',
  'Slippage tolerance exceeded': 'Price moved too much. Try again.',
};

// Wallet errors
const WALLET_ERRORS: Record<string, string> = {
  'User rejected': 'Transaction was cancelled.',
  'user rejected': 'Transaction was cancelled.',
  'rejected the request': 'Transaction was cancelled.',
  'User declined': 'Transaction was cancelled.',
  'Transaction cancelled': 'Transaction was cancelled.',
};

export interface MappedError {
  message: string;
  code?: string;
  recoverable: boolean;
  suggestion?: string;
  isInsufficientSol: boolean;
}

/**
 * Map a raw error to a user-friendly message
 */
export function mapTradeError(error: unknown): MappedError {
  const message = error instanceof Error ? error.message : String(error);
  const errorString = message.toLowerCase();

  // Check for user rejection (always recoverable)
  for (const [pattern, friendly] of Object.entries(WALLET_ERRORS)) {
    if (message.includes(pattern) || errorString.includes(pattern.toLowerCase())) {
      return {
        message: friendly,
        recoverable: true,
        isInsufficientSol: false,
      };
    }
  }

  // Check for Jupiter swap errors
  for (const [pattern, friendly] of Object.entries(JUPITER_ERRORS)) {
    if (message.includes(pattern) || errorString.includes(pattern.toLowerCase())) {
      return {
        message: friendly,
        recoverable: pattern === 'SLIPPAGE_EXCEEDED' || pattern === 'STALE_QUOTE' || pattern === 'TRANSACTION_EXPIRED',
        isInsufficientSol: false,
      };
    }
  }

  // Check for Meteora-specific errors
  for (const [code, friendly] of Object.entries(METEORA_ERRORS)) {
    if (message.includes(code)) {
      return {
        message: friendly,
        code,
        recoverable: code === '0x1771' || code === '0x1775', // Slippage errors are recoverable
        suggestion: code === '0x1770' ? 'Try trading 50% less.' : undefined,
        isInsufficientSol: false,
      };
    }
  }

  // Check for RPC errors
  for (const [pattern, friendly] of Object.entries(RPC_ERRORS)) {
    if (message.includes(pattern) || errorString.includes(pattern.toLowerCase())) {
      const isSolError = pattern.toLowerCase().includes('insufficient');
      return {
        message: friendly,
        recoverable: pattern.includes('block') || pattern.includes('Blockhash'),
        isInsufficientSol: isSolError,
      };
    }
  }

  // Check for network errors
  for (const [pattern, friendly] of Object.entries(NETWORK_ERRORS)) {
    if (message.includes(pattern) || errorString.includes(pattern.toLowerCase())) {
      return {
        message: friendly,
        recoverable: true,
        isInsufficientSol: false,
      };
    }
  }

  // Simulation failure
  if (errorString.includes('simulation') && errorString.includes('fail')) {
    return {
      message: 'Trade simulation failed. The pool may have insufficient liquidity.',
      recoverable: false,
      suggestion: 'Try a smaller trade amount.',
      isInsufficientSol: false,
    };
  }

  // Timeout
  if (errorString.includes('timeout') || errorString.includes('timed out')) {
    return {
      message: 'Transaction timed out. Check your wallet for status.',
      recoverable: true,
      isInsufficientSol: false,
    };
  }

  // Insufficient balance variations
  if (errorString.includes('insufficient') || errorString.includes('not enough')) {
    const isSolFees = errorString.includes('funds') || errorString.includes('lamports');
    return {
      message: isSolFees
        ? 'Not enough SOL for network fees. Deposit SOL to your wallet and try again.'
        : 'Insufficient balance for this trade.',
      recoverable: false,
      isInsufficientSol: isSolFees,
    };
  }

  // Program error extraction
  const programErrorMatch = message.match(/Program \w+ failed: (.+)/);
  if (programErrorMatch) {
    return {
      message: `Trade failed: ${programErrorMatch[1]}`,
      recoverable: false,
      isInsufficientSol: false,
    };
  }

  // Custom program error code extraction
  const customErrorMatch = message.match(/custom program error: (0x[0-9a-fA-F]+)/);
  if (customErrorMatch) {
    const code = customErrorMatch[1];
    return {
      message: `Trade failed with error code ${code}. Please try again.`,
      code,
      recoverable: true,
      isInsufficientSol: false,
    };
  }

  // Generic fallback
  return {
    message: 'Trade failed. Please try again or contact support.',
    recoverable: true,
    isInsufficientSol: false,
  };
}

/**
 * Check if an error is recoverable (worth retrying)
 */
export function isRecoverableError(error: unknown): boolean {
  const mapped = mapTradeError(error);
  return mapped.recoverable;
}

/**
 * Extract error code from message if present
 */
export function extractErrorCode(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error);

  // Hex error code
  const hexMatch = message.match(/0x[0-9a-fA-F]+/);
  if (hexMatch) return hexMatch[0];

  // Named error
  const namedMatch = message.match(/Error:\s*(\w+)/);
  if (namedMatch) return namedMatch[1];

  return null;
}

/**
 * Format error for Sentry reporting
 */
export function formatErrorForSentry(error: unknown): {
  message: string;
  code: string | null;
  raw: string;
} {
  const message = error instanceof Error ? error.message : String(error);
  const mapped = mapTradeError(error);
  const code = extractErrorCode(error);

  return {
    message: mapped.message,
    code,
    raw: message,
  };
}
